import { env } from '@/lib/env'
import { create } from 'zustand'
import { persist, type PersistStorage, type StorageValue } from 'zustand/middleware'

import type { Session, StageOption } from '@/features/auth/types'
import type { Workspace } from '@/types/workspace'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

interface AuthState {
  status: AuthStatus
  session: Session | null

  setSession: (session: Session) => void
  clearSession: () => void
  /** After a settings save: the server's resolved values, without signing in again. */
  setWorkspace: (workspace: Workspace) => void
  /** After a stage-name save: the board's column labels. */
  setStageNames: (stages: StageOption[]) => void
}

/** Only the session is written; `status` is derived from it on the way back in. */
type PersistedAuth = Pick<AuthState, 'session'>

/**
 * `createJSONStorage` lets a parse failure escape into the hydration promise,
 * where the rejection is swallowed and `status` stays `loading` — a white
 * screen with no way out. Unreadable storage is treated as empty instead:
 * being asked to sign in again is recoverable, a stuck loader is not.
 */
const safeStorage: PersistStorage<PersistedAuth> = {
  getItem: (name) => {
    try {
      const raw = localStorage.getItem(name)
      return raw ? (JSON.parse(raw) as StorageValue<PersistedAuth>) : null
    } catch {
      return null
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, JSON.stringify(value))
    } catch {
      // Private mode or a full quota — the session then lasts this tab only.
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name)
    } catch {
      // Nothing to do; the entry is unreachable either way.
    }
  },
}

/**
 * Who is signed in. Nothing here grants access — the credential is an HttpOnly
 * cookie the browser holds and this code cannot read.
 *
 * What is persisted is a *hint*: a reload paints the shell instead of flashing
 * the sign-in screen. The server stays the authority, and the first
 * unauthorised response clears it. There is no `GET /api/session` to ask the
 * server who we are, so this is as close to a session probe as we get.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      status: 'loading',
      session: null,

      setSession: (session) => set({ status: 'authenticated', session }),
      clearSession: () => set({ status: 'anonymous', session: null }),
      setWorkspace: (workspace) =>
        set((state) => (state.session ? { session: { ...state.session, workspace } } : state)),
      setStageNames: (stages) =>
        set((state) => (state.session ? { session: { ...state.session, stages } } : state)),
    }),
    {
      name: env.sharedShow
        ? 'ail.auth.shared-show'
        : env.complianceDevelopment
          ? 'ail.auth.compliance-development'
          : 'ail.auth',
      version: 4,
      storage: safeStorage,
      partialize: (state) => ({ session: state.session }),
      /**
       * v1 stored a bearer token and an Odoo-shaped user; v2 sessions predate
       * `locations`; v3 sessions predate `workspace`. None of these is worth
       * carrying over — a hint missing a field the shell reads is worse than
       * signing in again — and without this zustand warns about the missing
       * migration on every load.
       */
      migrate: () => ({ session: null }),
      /**
       * Storage has been read, so `loading` is over either way — and `status`
       * is derived here rather than in `onRehydrateStorage` because that
       * callback runs while `useAuthStore` is still being created. Any
       * reference to the store from there throws inside the hydration promise,
       * where the rejection is swallowed and the app waits on the loader
       * forever.
       */
      merge: (persisted, current) => {
        const session = (persisted as PersistedAuth | undefined)?.session ?? null
        return { ...current, session, status: session ? 'authenticated' : 'anonymous' }
      },
    },
  ),
)

export const selectIsAuthenticated = (state: AuthState) => state.status === 'authenticated'

export const useSession = () => useAuthStore((state) => state.session)
export const useCurrentUser = () => useAuthStore((state) => state.session?.user ?? null)
export const useAuthStatus = () => useAuthStore((state) => state.status)
export const useIsAuthenticated = () => useAuthStore(selectIsAuthenticated)
